import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';

import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/utils/window-globals';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ButtonDef {
    icon?: string;
    svg?: string;
    textLabel?: string;
    title: string;
    command: string;
    type: 'toggle' | 'button';
    extraClass?: string;
    tableOnly?: boolean;
    browserOnly?: boolean;
}

interface FontSizeOption {
    label: string;
    value: string;
}

interface FormatOption {
    label: string;
    value: string;
}

interface LineHeightOption {
    label: string;
    value: string;
}

export interface SelectionState {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    justifyLeft?: boolean;
    justifyCenter?: boolean;
    justifyRight?: boolean;
    justifyFull?: boolean;
    orderedList?: boolean;
    unorderedList?: boolean;
    upperRomanList?: boolean;
    lowerRomanList?: boolean;
    inLink?: boolean;
    inTable?: boolean;
    formatting?: string;
    fontName?: string;
    fontSize?: string;
    lineHeight?: string;
    foreColor?: string;
    backColor?: string;
    [key: string]: any;
}

export interface EditorToolbarProps {
    config: string[];
    selectionState: SelectionState;
    canUndo?: boolean;
    canRedo?: boolean;
    isReadOnly?: boolean;
    isMobile?: boolean;
    theme?: string;
    onCommand?: (data: { command: string; value?: any }) => void;
    onSaveSelection?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BUTTON_DEFS: Record<string, ButtonDef> = {
    bold: { icon: 'fa-bold', title: 'Bold (Ctrl+B)', command: 'bold', type: 'toggle' },
    italic: { icon: 'fa-italic', title: 'Italic (Ctrl+I)', command: 'italic', type: 'toggle' },
    underline: { icon: 'fa-underline', title: 'Underline (Ctrl+U)', command: 'underline', type: 'toggle' },
    strikethrough: { icon: 'fa-strikethrough', title: 'Strikethrough', command: 'strikethrough', type: 'toggle' },
    justifyLeft: { icon: 'fa-align-left', title: 'Align Left', command: 'justifyLeft', type: 'toggle' },
    justifyCenter: { icon: 'fa-align-center', title: 'Align Center', command: 'justifyCenter', type: 'toggle' },
    justifyRight: { icon: 'fa-align-right', title: 'Align Right', command: 'justifyRight', type: 'toggle' },
    justifyFull: { icon: 'fa-align-justify', title: 'Justify', command: 'justifyFull', type: 'toggle' },
    insertOrderedList: { icon: 'fa-list-ol', title: 'Numbered List (1, 2, 3)', command: 'insertOrderedList', type: 'toggle' },
    insertUnorderedList: { icon: 'fa-list-ul', title: 'Bullet List', command: 'insertUnorderedList', type: 'toggle' },
    insertUpperRomanList: { icon: '', title: 'Upper Roman List (I, II, III)', command: 'insertUpperRomanList', type: 'button', textLabel: 'I.' },
    insertLowerRomanList: { icon: '', title: 'Lower Roman List (i, ii, iii)', command: 'insertLowerRomanList', type: 'button', textLabel: 'i.' },
    indent: { icon: 'fa-indent', title: 'Indent', command: 'indent', type: 'button' },
    outdent: { icon: 'fa-outdent', title: 'Outdent', command: 'outdent', type: 'button' },
    createLink: { icon: 'fa-link', title: 'Insert Link', command: 'createLink', type: 'button' },
    unlink: { icon: 'fa-unlink', title: 'Remove Link', command: 'unlink', type: 'button' },
    insertImage: { icon: 'fa-image', title: 'Insert Image URL', command: 'insertImage', type: 'button' },
    uploadImage: { svg: '<svg viewBox="0 0 512 512"><path d="M272 160c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48m80 112-16 16-64 64-128-128L32 336V96.1l.1-.1h383.8l.1.1-.1 191.9H448V96c0-17.6-14.4-32-32-32H32C14.4 64 0 78.4 0 96v320c0 17.6 14.4 32 32 32h256v-64h64zm128 144h-32v-64h-32v64h-64v32h64v64h32v-64h64v-32z"></path></svg>', title: 'Upload Image', command: 'uploadImage', type: 'button' },
    insertFile: { icon: 'fa-file', title: 'Insert File', command: 'insertFile', type: 'button' },
    undo: { icon: 'fa-undo', title: 'Undo (Ctrl+Z)', command: 'undo', type: 'button' },
    redo: { icon: 'fa-redo', title: 'Redo (Ctrl+Y)', command: 'redo', type: 'button' },
    cleanFormatting: { icon: 'fa-eraser', title: 'Clear Formatting', command: 'cleanFormatting', type: 'button' },
    copyFormat: { icon: 'fa-paint-brush', title: 'Copy Format', command: 'copyFormat', type: 'button' },
    applyFormat: { icon: 'fa-fill-drip', title: 'Apply Format', command: 'applyFormat', type: 'button' },
    // Table buttons
    createTable: { icon: 'fa-table', title: 'Insert Table', command: 'createTable', type: 'button' },
    addRowAbove: { icon: 'fa-arrow-up', title: 'Add Row Above', command: 'addRowAbove', type: 'button', tableOnly: true },
    addRowBelow: { icon: 'fa-arrow-down', title: 'Add Row Below', command: 'addRowBelow', type: 'button', tableOnly: true },
    addColumnLeft: { icon: 'fa-arrow-left', title: 'Add Column Left', command: 'addColumnLeft', type: 'button', tableOnly: true },
    addColumnRight: { icon: 'fa-arrow-right', title: 'Add Column Right', command: 'addColumnRight', type: 'button', tableOnly: true },
    deleteRow: { icon: 'fa-minus', title: 'Delete Row', command: 'deleteRow', type: 'button', tableOnly: true },
    deleteColumn: { icon: 'fa-columns', title: 'Delete Column', command: 'deleteColumn', type: 'button', tableOnly: true },
    mergeCellsHorizontally: { icon: 'fa-arrows-alt-h', title: 'Merge Cells Horizontally', command: 'mergeCellsHorizontally', type: 'button', tableOnly: true },
    mergeCellsVertically: { icon: 'fa-arrows-alt-v', title: 'Merge Cells Vertically', command: 'mergeCellsVertically', type: 'button', tableOnly: true },
    splitCellHorizontally: { icon: 'fa-grip-lines', title: 'Split Cell Horizontally', command: 'splitCellHorizontally', type: 'button', tableOnly: true },
    splitCellVertically: { icon: 'fa-grip-lines-vertical', title: 'Split Cell Vertically', command: 'splitCellVertically', type: 'button', tableOnly: true },
    tableAlignLeft: { icon: 'fa-align-left', title: 'Align Table Left', command: 'tableAlignLeft', type: 'button', tableOnly: true },
    tableAlignCenter: { icon: 'fa-align-center', title: 'Align Table Center', command: 'tableAlignCenter', type: 'button', tableOnly: true },
    tableAlignRight: { icon: 'fa-align-right', title: 'Align Table Right', command: 'tableAlignRight', type: 'button', tableOnly: true },
    // Print / Export buttons
    print: { icon: 'fa-print', title: 'Print', command: 'print', type: 'button' },
    exportPdf: { icon: 'fa-file-pdf', title: 'Export to PDF', command: 'exportPdf', type: 'button', browserOnly: true },
};

const FONT_NAMES: string[] = [
    'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New',
    'Georgia', 'Impact', 'Lucida Console', 'Palatino Linotype',
    'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana',
];

const FONT_SIZES: FontSizeOption[] = [
    { label: '8pt', value: '1' },
    { label: '10pt', value: '2' },
    { label: '12pt', value: '3' },
    { label: '14pt', value: '4' },
    { label: '18pt', value: '5' },
    { label: '24pt', value: '6' },
    { label: '36pt', value: '7' },
];

const FORMAT_OPTIONS: FormatOption[] = [
    { label: 'Paragraph', value: 'p' },
    { label: 'Heading 1', value: 'h1' },
    { label: 'Heading 2', value: 'h2' },
    { label: 'Heading 3', value: 'h3' },
    { label: 'Heading 4', value: 'h4' },
    { label: 'Heading 5', value: 'h5' },
    { label: 'Heading 6', value: 'h6' },
];

const LINE_HEIGHTS: LineHeightOption[] = [
    { label: '1', value: '1' },
    { label: '1.15', value: '1.15' },
    { label: '1.5', value: '1.5' },
    { label: '2', value: '2' },
    { label: '2.5', value: '2.5' },
    { label: '3', value: '3' },
];

const STATE_MAP: Record<string, string> = {
    insertUnorderedList: 'unorderedList',
    insertUpperRomanList: 'upperRomanList',
    insertLowerRomanList: 'lowerRomanList',
    createLink: 'inLink',
};

const COMMAND_MAP: Record<string, string> = {
    formatting: 'setFormatting',
    fontName: 'setFontName',
    fontSize: 'setFontSize',
    lineHeight: 'setLineHeight',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditorToolbar({
    config,
    selectionState,
    canUndo = false,
    canRedo = false,
    isReadOnly = false,
    isMobile = false,
    theme: themeProp,
    onCommand,
    onSaveSelection,
}: EditorToolbarProps) {
    const { theme: contextTheme } = useTheme();
    const theme = themeProp !== undefined ? themeProp : contextTheme;

    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [dropdownStyle, setDropdownStyle] = useState<Record<string, string>>({});

    const toolbarRef = useRef<HTMLDivElement>(null);
    const portalContainerRef = useRef<HTMLDivElement | null>(null);

    // -----------------------------------------------------------------------
    // Computed / derived
    // -----------------------------------------------------------------------

    const inTable = useMemo(() => selectionState.inTable || false, [selectionState.inTable]);

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    const isButtonActive = useCallback(
        (buttonName: string): boolean => {
            // Special handling for ordered list button - only active if decimal (not roman)
            if (buttonName === 'insertOrderedList') {
                return !!(
                    selectionState.orderedList &&
                    !selectionState.upperRomanList &&
                    !selectionState.lowerRomanList
                );
            }
            const stateName = STATE_MAP[buttonName] || buttonName;
            if (selectionState[stateName] !== undefined) {
                return !!selectionState[stateName];
            }
            return false;
        },
        [selectionState],
    );

    const isButtonHidden = useCallback(
        (buttonName: string): boolean => {
            const def = BUTTON_DEFS[buttonName];
            if (def?.tableOnly && !inTable) return true;
            if (
                def?.browserOnly &&
                (typeof isUWPApp !== 'undefined' && isUWPApp ||
                 typeof isIOSApp !== 'undefined' && isIOSApp ||
                 typeof isAndroidApp !== 'undefined' && isAndroidApp ||
                 typeof isWindowsAppSdkApp !== 'undefined' && isWindowsAppSdkApp)
            ) {
                return true;
            }
            return false;
        },
        [inTable],
    );

    const isButtonDisabled = useCallback(
        (buttonName: string): boolean => {
            if (buttonName === 'print' || buttonName === 'exportPdf') return false;
            if (isReadOnly) return true;
            if (buttonName === 'undo') return !canUndo;
            if (buttonName === 'redo') return !canRedo;
            return false;
        },
        [isReadOnly, canUndo, canRedo],
    );

    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------

    const closeDropdowns = useCallback(() => {
        setOpenDropdown(null);
    }, []);

    const handleButtonClick = useCallback(
        (command: string) => {
            if (isReadOnly && command !== 'print' && command !== 'exportPdf') return;
            onCommand?.({ command });
            closeDropdowns();
        },
        [isReadOnly, onCommand, closeDropdowns],
    );

    const handleDropdownSelect = useCallback(
        (command: string, value: any) => {
            if (isReadOnly) return;
            onCommand?.({ command, value });
            closeDropdowns();
        },
        [isReadOnly, onCommand, closeDropdowns],
    );

    const toggleDropdown = useCallback(
        (name: string, event?: React.MouseEvent | React.TouchEvent) => {
            setOpenDropdown((prev) => {
                if (prev === name) {
                    setDropdownStyle({});
                    return null;
                }
                if (event) {
                    const button = event.currentTarget as HTMLElement;
                    if (button) {
                        const rect = button.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                        setDropdownStyle({
                            top: (rect.bottom + scrollTop + 2) + 'px',
                            left: Math.max(8, rect.left + scrollLeft) + 'px',
                        });
                    }
                } else {
                    setDropdownStyle({});
                }
                return name;
            });
        },
        [],
    );

    const saveSelectionAndToggle = useCallback(
        (name: string, event: React.MouseEvent | React.TouchEvent) => {
            toggleDropdown(name, event);
        },
        [toggleDropdown],
    );

    // -----------------------------------------------------------------------
    // Portal container lifecycle
    // -----------------------------------------------------------------------

    useEffect(() => {
        try {
            if (!document.body) return;
            const container = document.createElement('div');
            container.className = 'kst-toolbar-dropdown-portal';
            if (theme) {
                container.classList.add(theme);
            }
            document.body.appendChild(container);
            portalContainerRef.current = container;
        } catch (e) {
            console.warn('Failed to create portal container:', e);
        }
        return () => {
            const container = portalContainerRef.current;
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
            portalContainerRef.current = null;
        };
    }, []); // mount / unmount only

    // Keep theme class in sync on portal container
    useEffect(() => {
        const container = portalContainerRef.current;
        if (!container) return;
        container.className = 'kst-toolbar-dropdown-portal';
        if (theme) {
            container.classList.add(theme);
        }
    }, [theme]);

    // -----------------------------------------------------------------------
    // Click-outside handler
    // -----------------------------------------------------------------------

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                toolbarRef.current &&
                !toolbarRef.current.contains(target) &&
                (!portalContainerRef.current || !portalContainerRef.current.contains(target))
            ) {
                closeDropdowns();
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [closeDropdowns]);

    // -----------------------------------------------------------------------
    // Dropdown items derivation
    // -----------------------------------------------------------------------

    const dropdownItems = useMemo(() => {
        if (!openDropdown) return null;

        let items: { label: string; value: string; style?: string }[] = [];
        let activeValue = '';

        if (openDropdown === 'formatting') {
            items = FORMAT_OPTIONS;
            activeValue = selectionState.formatting || 'p';
        } else if (openDropdown === 'fontName') {
            items = FONT_NAMES.map((f) => ({ label: f, value: f, style: 'font-family: ' + f }));
            activeValue = selectionState.fontName || 'Arial';
        } else if (openDropdown === 'fontSize') {
            items = FONT_SIZES;
            activeValue = selectionState.fontSize || '3';
        } else if (openDropdown === 'lineHeight') {
            items = LINE_HEIGHTS;
            activeValue = selectionState.lineHeight || '1.5';
        }

        const command = COMMAND_MAP[openDropdown];
        if (!command) return null;

        return { items, activeValue, command };
    }, [openDropdown, selectionState.formatting, selectionState.fontName, selectionState.fontSize, selectionState.lineHeight]);

    // -----------------------------------------------------------------------
    // Dropdown portal rendering
    // -----------------------------------------------------------------------

    const dropdownPortal = useMemo(() => {
        if (!dropdownItems || !portalContainerRef.current) return null;

        const { items, activeValue, command } = dropdownItems;
        const dropdownClass =
            openDropdown === 'fontName'
                ? 'toolbar-dropdown font-dropdown'
                : 'toolbar-dropdown';
        const themeClass = theme ? ' ' + theme : '';

        return ReactDOM.createPortal(
            <div
                className={dropdownClass + themeClass}
                style={{
                    position: 'absolute',
                    top: dropdownStyle.top,
                    left: dropdownStyle.left,
                    zIndex: 99999,
                    background: '#fff',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '120px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                }}
            >
                {items.map((item) => {
                    const isActive = item.value === activeValue;
                    return (
                        <div
                            key={item.value}
                            className={'toolbar-dropdown-item' + (isActive ? ' active' : '')}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                ...(item.style ? parseCSSString(item.style) : {}),
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleDropdownSelect(command, item.value);
                            }}
                            onTouchEnd={(e) => {
                                e.preventDefault();
                                handleDropdownSelect(command, item.value);
                            }}
                        >
                            {item.label}
                        </div>
                    );
                })}
            </div>,
            portalContainerRef.current,
        );
    }, [dropdownItems, openDropdown, dropdownStyle, theme, handleDropdownSelect]);

    // -----------------------------------------------------------------------
    // Toolbar mousedown handler (save-selection)
    // -----------------------------------------------------------------------

    const handleToolbarMouseDown = useCallback(() => {
        onSaveSelection?.();
    }, [onSaveSelection]);

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const renderItem = (item: string, index: number) => {
        // Separator
        if (item === 'separator') {
            return <div key={'sep-' + index} className="toolbar-separator" />;
        }

        // Formatting Dropdown
        if (item === 'formatting') {
            const currentLabel =
                FORMAT_OPTIONS.find((f) => f.value === selectionState.formatting)?.label || 'Paragraph';
            return (
                <div
                    key={item}
                    className={'toolbar-dropdown-container' + (openDropdown === 'formatting' ? ' is-open' : '')}
                >
                    <button
                        className="toolbar-button toolbar-dropdown-trigger"
                        disabled={isReadOnly}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('formatting', e);
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('formatting', e);
                        }}
                        title="Format Block"
                    >
                        <span className="dropdown-label">{currentLabel}</span>
                        <i className="fas fa-caret-down" />
                    </button>
                </div>
            );
        }

        // Font Name Dropdown
        if (item === 'fontName') {
            return (
                <div
                    key={item}
                    className={'toolbar-dropdown-container' + (openDropdown === 'fontName' ? ' is-open' : '')}
                >
                    <button
                        className="toolbar-button toolbar-dropdown-trigger"
                        disabled={isReadOnly}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('fontName', e);
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('fontName', e);
                        }}
                        title="Font Family"
                    >
                        <span className="dropdown-label">{selectionState.fontName || 'Arial'}</span>
                        <i className="fas fa-caret-down" />
                    </button>
                </div>
            );
        }

        // Font Size Dropdown
        if (item === 'fontSize') {
            const currentLabel =
                FONT_SIZES.find((s) => s.value === selectionState.fontSize)?.label || '12pt';
            return (
                <div
                    key={item}
                    className={'toolbar-dropdown-container' + (openDropdown === 'fontSize' ? ' is-open' : '')}
                >
                    <button
                        className="toolbar-button toolbar-dropdown-trigger"
                        disabled={isReadOnly}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('fontSize', e);
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('fontSize', e);
                        }}
                        title="Font Size"
                    >
                        <span className="dropdown-label">{currentLabel}</span>
                        <i className="fas fa-caret-down" />
                    </button>
                </div>
            );
        }

        // Line Height Dropdown
        if (item === 'lineHeight') {
            return (
                <div
                    key={item}
                    className={'toolbar-dropdown-container' + (openDropdown === 'lineHeight' ? ' is-open' : '')}
                >
                    <button
                        className="toolbar-button toolbar-dropdown-trigger"
                        disabled={isReadOnly}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('lineHeight', e);
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            saveSelectionAndToggle('lineHeight', e);
                        }}
                        title="Line Height"
                    >
                        <i className="fas fa-text-height" />
                        <i className="fas fa-caret-down" />
                    </button>
                </div>
            );
        }

        // Fore Color Button
        if (item === 'foreColor') {
            return (
                <button
                    key={item}
                    className="toolbar-button color-button"
                    disabled={isReadOnly}
                    onClick={() => handleButtonClick('openForeColorPicker')}
                    title="Text Color"
                >
                    <i className="fas fa-font" />
                    <span
                        className="color-indicator"
                        style={{ backgroundColor: selectionState.foreColor || '#000000' }}
                    />
                </button>
            );
        }

        // Back Color Button
        if (item === 'backColor') {
            return (
                <button
                    key={item}
                    className="toolbar-button color-button"
                    disabled={isReadOnly}
                    onClick={() => handleButtonClick('openBackColorPicker')}
                    title="Background Color"
                >
                    <i className="fas fa-highlighter" />
                    <span
                        className="color-indicator"
                        style={{ backgroundColor: selectionState.backColor || 'transparent' }}
                    />
                </button>
            );
        }

        // Standard Buttons
        const def = BUTTON_DEFS[item];
        if (def && !isButtonHidden(item)) {
            const active = isButtonActive(item);
            const disabled = isButtonDisabled(item);

            let className = 'toolbar-button';
            if (active) className += ' active';
            if (def.extraClass) className += ' ' + def.extraClass;
            if (def.textLabel) className += ' text-label-button';

            return (
                <button
                    key={item}
                    className={className}
                    disabled={disabled}
                    onClick={() => handleButtonClick(def.command)}
                    title={def.title}
                >
                    {def.icon ? (
                        <i className={'fas ' + def.icon} />
                    ) : def.svg ? (
                        <span
                            className="button-svg-icon"
                            dangerouslySetInnerHTML={{ __html: def.svg }}
                        />
                    ) : def.textLabel ? (
                        <span className="button-text-label">{def.textLabel}</span>
                    ) : null}
                </button>
            );
        }

        // Unknown item -- skip
        return null;
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <>
            <div
                ref={toolbarRef}
                className={'toolbar' + (isMobile ? ' toolbar-mobile' : '')}
                onMouseDown={handleToolbarMouseDown}
            >
                {config.map((item, index) => renderItem(item, index))}
            </div>
            {dropdownPortal}
        </>
    );
}

// ---------------------------------------------------------------------------
// Utility: parse a simple CSS string like "font-family: Arial" into an object
// ---------------------------------------------------------------------------

function parseCSSString(css: string): Record<string, string> {
    const result: Record<string, string> = {};
    const parts = css.split(';');
    for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;
        const colonIdx = trimmed.indexOf(':');
        if (colonIdx === -1) continue;
        const prop = trimmed.substring(0, colonIdx).trim();
        const value = trimmed.substring(colonIdx + 1).trim();
        // Convert CSS property name to camelCase
        const camelProp = prop.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());
        result[camelProp] = value;
    }
    return result;
}

// ---------------------------------------------------------------------------
// Global registration
// ---------------------------------------------------------------------------

(window as any).EditorToolbarReact = EditorToolbar;
